import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { IMeta, apiManager } from '../../../../REST';
import { INameObjectN } from '../../../../interfaces';
import { mapApi2UiList } from '../../../../instruments';
import { tB, tF } from '../../../../translate';
import { Catcher, ILoadMoreResult, InfiniteTable, LinkComponent } from '../../../../Components';

type IListResponse = {
    cards?: any[];
    meta?: IMeta;
};

type ITariffCard = {
    id?: number;
    name?: string;
    cardSets?: INameObjectN[];
    currentTariff?: INameObjectN;
    iccId?: string;
    msisdn?: string;
    nextTariff?: INameObjectN;
    printedId?: string;
    shortId?: string;
};

type IDataType = {
    cardLink: ReactNode;
    shortIdLink: ReactNode;
    msisdnLink: ReactNode;
    setList: ReactNode;
    currentTariffLink: ReactNode;
    nextTariffLink?: ReactNode;
};

type IAssignTariffCards = {
    isNeedOpen: boolean;
    setIsNeedOpen: (isOpen: boolean) => void;
    companyId?: string;
    cardsId?: string[];
    cardSetsId?: string[];
    runBuy?: () => void;
};

export const AssignTariffCardsModal: React.FC<IAssignTariffCards> = ({
    isNeedOpen,
    setIsNeedOpen,
    companyId,
    cardsId,
    cardSetsId,
    runBuy,
}) => {
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [recordsCount, setRecordsCount] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [list, setList] = useState<ITariffCard[]>([]);
    const pageSize = 10;

    const closeAll = () => {
        setIsOpenModal(false);
        setRecordsCount(0);
        setIsLoaded(false);
        setList([]);
        setIsNeedOpen(false);
    };

    const clickBuy = () => {
        runBuy?.();
        closeAll();
    };

    const loadMore = async (pageNumber = 1): Promise<ILoadMoreResult> => {
        try {
            const { resp } = await apiManager.getApi('GET_TARIFF_CARDS', {
                searchParamsList: {
                    pageNumber,
                    pageSize,
                    assignedCompanyId: companyId,
                    ids: cardsId,
                    cardSetId: cardSetsId,
                },
            });
            const { cards, meta } = resp as IListResponse;
            setRecordsCount(meta?.records_count || 0);
            const list = mapApi2UiList(cards) as ITariffCard[];
            setList((prevList) => [...prevList, ...list]);
            setIsLoaded(true);
            return {
                totalCount: meta?.records_count || 0,
            };
        } catch (error) {
            console.error('Error loading list:', error);
            return {
                totalCount: 0,
            };
        }
    };

    useEffect(() => {
        if (isNeedOpen) {
            loadMore();
        }
    }, [isNeedOpen]);

    useEffect(() => {
        if (isLoaded) {
            if (list.length > 0) {
                setIsOpenModal(true);
            } else {
                clickBuy();
            }
        }
    }, [isLoaded, list.length]);

    const columns: ColumnsType<IDataType> = [
        {
            title: tF('Card'),
            dataIndex: 'cardLink',
        },
        {
            title: tF('Short ID'),
            dataIndex: 'shortIdLink',
        },
        {
            title: tF('MSISDN'),
            dataIndex: 'msisdnLink',
        },
        {
            title: tF('Card Sets'),
            dataIndex: 'setList',
        },
        {
            title: tF('Current month'),
            dataIndex: 'currentTariffLink',
        },
        {
            title: tF('Next month'),
            dataIndex: 'nextTariffLink',
        },
    ];

    const dataSource: IDataType[] = list.map((card: ITariffCard) => {
        const CardSetListLinks =
            card.cardSets?.map((s) => {
                return (
                    <LinkComponent currentListComponent={'CardSetsList'} id={s.id} isLight name={s.name} key={s.id} />
                );
            }) || null;
        return {
            key: card.id,
            cardLink: <LinkComponent currentListComponent={'CardList'} id={card.id} name={card.name} />,
            shortIdLink: (
                <LinkComponent
                    currentListComponent={'CardList'}
                    id={card.id}
                    name={card.shortId || card.printedId}
                    tooltip={card.printedId ? `${tF('Printed ID')} ${card.printedId}` : undefined}
                />
            ),
            msisdnLink: card.msisdn ? (
                <LinkComponent currentListComponent={'CardList'} id={card.id} name={card.msisdn} />
            ) : null,
            setList: CardSetListLinks,
            currentTariffLink: card.currentTariff?.id ? (
                <LinkComponent
                    currentListComponent={'TariffsList'}
                    id={card.currentTariff?.id}
                    name={card.currentTariff?.name}
                />
            ) : null,
            nextTariffLink: card.nextTariff?.id ? (
                <LinkComponent
                    currentListComponent={'TariffsList'}
                    id={card.nextTariff?.id}
                    name={card.nextTariff?.name}
                />
            ) : null,
        };
    });

    return (
        <Catcher>
            <Modal
                title={`${tF('Assign Tariff Cards')} ${recordsCount ? ': ' + recordsCount : ''}`}
                open={isOpenModal}
                onCancel={closeAll}
                wrapClassName={'modal'}
                footer={[
                    <Button key="back" onClick={closeAll}>
                        {tB('Cancel')}
                    </Button>,
                    <Button key="buy" type="primary" onClick={clickBuy} disabled={!runBuy}>
                        {tB('Buy')}
                    </Button>,
                ]}
                destroyOnClose
                width={900}
                centered
            >
                <InfiniteTable<IDataType>
                    columns={columns}
                    dataSource={dataSource}
                    idSpinners={['GET_TARIFF_CARDS']}
                    loadMore={loadMore}
                    resetKey={`${companyId}-${cardsId?.join(',')}-${cardSetsId?.join(',')}`}
                    scrollY={300}
                />
            </Modal>
        </Catcher>
    );
};
